import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Sparkles, Crown, Infinity as InfinityIcon, Palette, Users, MessageCircle, Target, Brain,
} from "lucide-react";
import { useAccess } from "../context/AccessContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { trackEvent, EVENTS } from "../lib/analytics";

// One-time welcome popup for the 30-Day Premium Experience PLUS the
// "What are you preparing for?" goal picker. Both are dismissed together via
// a single localStorage flag so users only see this onboarding once.
//
// We bump the localStorage key whenever the popup copy meaningfully changes
// so production users still get the new version once. v3: Jun 2026, raised
// trial from 10 → 30 days under the Founder's Reward / new-trial campaign.
const FLAG = "syllox_welcome_popup_seen_v3";

const PERKS = [
  { icon: InfinityIcon, label: "Unlimited subjects & chapters" },
  { icon: Brain,        label: "AI doubt solving" },
  { icon: Palette,      label: "Premium themes" },
  { icon: MessageCircle, label: "Community access" },
  { icon: Users,        label: "Friends system" },
  { icon: Target,       label: "Today's Targets & daily focus" },
];

const GOALS = ["NEET", "JEE", "UPSC", "SSC", "Other"];

export default function WelcomeTrialPopup() {
  const { access } = useAccess();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!access?.in_trial) return;
    // Don't compete with the higher-priority Founder's Reward popup. If the
    // user is an existing-user-getting-the-reward, that popup runs first and
    // its dismissal handler refreshes /me/access, which then clears
    // founders_reward_pending and lets this popup decide its own fate.
    if (access?.founders_reward_pending) return;
    if (localStorage.getItem(FLAG)) return;
    if (user?.exam_track) return; // already personalized in a previous session — skip
    // Tiny delay so the dashboard paints first, then the popup opens —
    // feels less abrupt than an instant modal on first load.
    const t = setTimeout(() => {
      setOpen(true);
      trackEvent(EVENTS.PREMIUM_TRIAL_30D_STARTED, {
        trial_days_left: access?.trial_days_left,
      });
    }, 600);
    return () => clearTimeout(t);
  }, [access?.in_trial, access?.founders_reward_pending, access?.trial_days_left, user?.exam_track]);

  // Persist exam_track (if user picked one) and stamp the localStorage flag
  // so this popup never re-opens for this user on this device.
  const persistAndClose = async ({ navigateAfter }) => {
    localStorage.setItem(FLAG, "1");
    setOpen(false);
    if (selectedGoal && selectedGoal !== user?.exam_track) {
      setSaving(true);
      try {
        const { data } = await api.patch("/users/me", { exam_track: selectedGoal });
        setUser(data);
      } catch {
        // Non-critical — user can re-pick in Settings later.
      } finally {
        setSaving(false);
      }
    }
    if (navigateAfter) navigate(navigateAfter);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : persistAndClose({}))}>
      <DialogContent
        className="max-w-md gap-0 overflow-hidden rounded-3xl border-emerald-500/30 bg-white p-0 shadow-2xl shadow-emerald-500/10 dark:bg-zinc-950"
        data-testid="welcome-trial-popup"
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 px-6 py-7 text-white">
          <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/10 blur-2xl" aria-hidden="true"/>
          <div className="relative flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/15 backdrop-blur-sm">
              <Crown size={22}/>
            </div>
            <div>
              <DialogTitle className="font-heading text-xl font-bold text-white">
                🎉 Welcome to Syllox Premium
              </DialogTitle>
              <p className="text-xs text-emerald-50/90">Your 30-Day Premium Trial</p>
            </div>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
          <DialogDescription className="text-sm text-zinc-600 dark:text-zinc-400">
            Your 30-Day Premium Trial has been activated. Enjoy full access to all Premium features and make the most of your preparation journey.
          </DialogDescription>

          <ul className="mt-5 grid grid-cols-1 gap-2.5 text-sm">
            {PERKS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2.5 text-zinc-700 dark:text-zinc-300">
                <Icon size={16} className="shrink-0 text-emerald-500"/>
                {label}
              </li>
            ))}
          </ul>

          {/* Goal selection — lightweight personalization. Optional. */}
          <div className="mt-6 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-4 dark:border-zinc-800 dark:bg-zinc-900/40" data-testid="goal-picker">
            <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">What are you preparing for?</div>
            <p className="mt-0.5 text-xs text-zinc-500">Helps us personalize your dashboard. You can skip this.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {GOALS.map((g) => {
                const active = selectedGoal === g;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setSelectedGoal(active ? null : g)}
                    data-testid={`goal-chip-${g.toLowerCase()}`}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                      active
                        ? "border-emerald-500 bg-emerald-500 text-white shadow-sm shadow-emerald-500/30"
                        : "border-zinc-300 bg-white text-zinc-700 hover:border-emerald-400 hover:text-emerald-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-emerald-500/40"
                    }`}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => persistAndClose({})}
              disabled={saving}
              data-testid="welcome-popup-maybe-later"
              className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              {selectedGoal ? "Skip for now" : "Maybe later"}
            </Button>
            <Button
              type="button"
              onClick={() => persistAndClose({ navigateAfter: "/syllabus" })}
              disabled={saving}
              data-testid="welcome-popup-start"
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 text-white shadow-md shadow-emerald-500/30 hover:from-emerald-600 hover:to-emerald-700"
            >
              <Sparkles size={14} className="mr-1.5"/>
              {saving ? "Saving…" : "Let's Go 🚀"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
